import type { WeekCellModel } from '../model/weekTypes';
import { MUTED_TINT } from '../model/mutedTint';
import { WeekEventChip } from './WeekEventChip';

interface Props {
  cell: WeekCellModel;
  technicianId: number;
  /** Takes the user to the day board for this cell's day, with this technician in view. */
  onOpenDay: (dayIso: string, technicianId: number) => void;
  /** The Handwerker does not cover the Gewerk being planned — tinted, still clickable. */
  muted?: boolean;
}

/**
 * One technician on one weekday.
 *
 * The whole cell is the click target, not the chips inside it: the week view is a read surface, and
 * everything you would want to *do* with an entry lives on the day board.
 */
export const WeekCell = ({ cell, technicianId, onOpenDay, muted = false }: Props) => {
  const isEmpty = cell.fullDayAbsences.length === 0 && cell.chips.length === 0;

  return (
    <button
      type="button"
      onClick={() => onOpenDay(cell.dayIso, technicianId)}
      aria-label={`${cell.dayIso} öffnen`}
      className={[
        'group relative flex min-h-[92px] w-full flex-col gap-1 border-b border-r border-slate-200 p-1.5 text-left align-top',
        'transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#004494]',
        // A day the technician does not work is shaded end to end, the same signal
        // `BoardShiftOverlay` gives on the day board.
        cell.working ? 'bg-white hover:bg-slate-50' : 'bg-slate-100/70 hover:bg-slate-100',
        cell.isToday && cell.working ? 'bg-[#004494]/[0.04]' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {muted && <div aria-hidden className={`pointer-events-none absolute inset-0 ${MUTED_TINT}`} />}

      {/*
        Full-day absences first and in the same filled treatment as everything else — they are the
        state of the day, so they read top-down before the work that survived them. More than one is
        rare but legal: a Urlaub ending as a Krankmeldung starts.
      */}
      {cell.fullDayAbsences.map((chip) => (
        <WeekEventChip key={chip.key} chip={chip} fill={chip.fillsCell} />
      ))}

      {cell.chips.map((chip) => (
        <WeekEventChip key={chip.key} chip={chip} />
      ))}

      {isEmpty && cell.working && (
        <span className="m-auto font-['Roboto'] text-[11px] text-slate-300 group-hover:text-slate-400">
          frei
        </span>
      )}
    </button>
  );
};
